import { messages } from '../../components/country-names.messages';
import internationalMessages from '../../pages/settings/international/messages';
import { SellerSettings } from '../../enums/seller-settings/seller-settings';
import { SellerSettingsOptions } from '../../enums/seller-settings/seller-settings-options';
import { UspsExemptionCodes } from '../../enums/seller-settings/usps-exemption-codes';
import { labelFileTypeValues } from '../../pages/settings/connect/constants';
import {
  allLabelFormats,
  labelDpis,
  batchSummaryOptions,
  labelOrderBys,
  customLabelLayoutSettingOptions,
} from '../../pages/settings/printing/document-option-modal/label/constants';
import { orderReportSortings } from '../../pages/settings/printing/document-option-modal/order-summary/constants';
import {
  defaultPackingSlipFormats,
  newPackingSlipFormats,
  packingSlipSortings,
} from '../../pages/settings/printing/document-option-modal/packing-slip/constants';
import {
  pickListGroupings,
  pickListSortings,
  pickListWarehouseLocations,
} from '../../pages/settings/printing/document-option-modal/pick-list/constants';
import { EnabledOnboardingVariantTypes } from '../../pages/setup/constants';
import { PackingEnforcementMode } from '../../packing/enforcement/constants';
import { SerialEnforcementLevel } from '../../serial-numbers/enforcement/constants';

import type { MessageDescriptor } from 'react-intl';

export type SellerSettingOption = {
  value: string | boolean;
  // Optional because `seller-settings-options.js` attaches it at runtime, to the labelled options only.
  messageDescriptor?: MessageDescriptor;
};

export type LabelledSellerSettingOption = SellerSettingOption & { messageDescriptor: MessageDescriptor };

// Two settings break the flat-list shape: `customLabelLayouts` nests lists and `totalOrderWithSmartFillApplied` holds one option.
export type SellerSettingOptionSet = SellerSettingOption[] | SellerSettingOption | unknown[][];

/** Anything with a `key` addresses the map; in practice these are the `SellerSettings` entries. */
export type SellerSettingKey = {
  key: string;
};

const trueFalse: SellerSettingOption[] = [{ value: true }, { value: false }];

export const sellerSettingsOptionsMap: Record<string, SellerSettingOptionSet> = {
  [SellerSettings.dimensionUnitOfMeasure.key]: [SellerSettingsOptions.inch, SellerSettingsOptions.centimeter],
  [SellerSettings.weightUnitOfMeasure.key]: [SellerSettingsOptions.ounce, SellerSettingsOptions.gram],
  [SellerSettings.useAdvancedProductDisplay.key]: [SellerSettingsOptions.standard, SellerSettingsOptions.advanced],
  [SellerSettings.addToWebHook.key]: [
    SellerSettingsOptions.onNewOrders,
    SellerSettingsOptions.onNewItems,
    SellerSettingsOptions.onOrdersShipped,
    SellerSettingsOptions.onItemsShipped,
    SellerSettingsOptions.onFulfillmentsShipped,
    SellerSettingsOptions.onFulfillmentsRejected,
    SellerSettingsOptions.onBatchProcessedV2,
    SellerSettingsOptions.onTrackEventV2,
    SellerSettingsOptions.onNewOrderCreatedV2,
    SellerSettingsOptions.onNewLabelCreatedV2,
    SellerSettingsOptions.onFulfillmentsShippedV2,
    SellerSettingsOptions.onFulfillmentsRejectedV2,
  ],
  [SellerSettings.customsContentType.key]: [
    SellerSettingsOptions.documents,
    SellerSettingsOptions.gift,
    SellerSettingsOptions.merchandise,
    SellerSettingsOptions.returnedGoods,
    SellerSettingsOptions.sample,
  ],
  [SellerSettings.customsNonDelivery.key]: [SellerSettingsOptions.return, SellerSettingsOptions.abandon],
  [SellerSettings.createCustomsItems.key]: [
    SellerSettingsOptions.orderItems,
    SellerSettingsOptions.preDefinedValues,
    SellerSettingsOptions.leaveBlank,
  ],
  [SellerSettings.customsItemDescription.key]: [],
  [SellerSettings.customsItemQty.key]: [],
  [SellerSettings.customsItemValue.key]: [],
  [SellerSettings.customsItemCountry.key]: [
    { value: SellerSettingsOptions.empty.value, messageDescriptor: internationalMessages.blankCountryOption },
    ...Object.entries(messages).map(([countryCode, messageDescriptor]) => ({
      value: countryCode,
      messageDescriptor,
    })),
  ],
  [SellerSettings.customsItemHarmonization.key]: [],
  [SellerSettings.customsSigner.key]: [],
  [SellerSettings.exemptionOrITN.key]: [
    SellerSettingsOptions.empty,
    SellerSettingsOptions.itn,
    SellerSettingsOptions.uspsExemptionCode,
    SellerSettingsOptions.vatNumber,
  ],
  [SellerSettings.iTNTaxId.key]: [], // while this is a seller setting, it's not actually sent to the UI as such
  [SellerSettings.iTNSetting.key]: [
    SellerSettingsOptions.empty,
    SellerSettingsOptions.lineItems,
    SellerSettingsOptions.allInternationalShipments,
  ],
  [SellerSettings.defaultExemptionCode.key]: UspsExemptionCodes,
  [SellerSettings.fCMITracking.key]: trueFalse,
  [SellerSettings.InventoryShipNoStock.key]: [
    SellerSettingsOptions.allowShip,
    SellerSettingsOptions.warnShip,
    SellerSettingsOptions.errorShip,
  ],
  [SellerSettings.InventoryShipUnknown.key]: [
    SellerSettingsOptions.allowShip,
    SellerSettingsOptions.warnShip,
    SellerSettingsOptions.errorShip,
  ],
  [SellerSettings.InventoryReOrderThreshold.key]: [],
  [SellerSettings.InventoryShipNoWarehouse.key]: trueFalse,
  [SellerSettings.InventoryTrackingEnabled.key]: trueFalse,
  [SellerSettings.orderReportSorting.key]: orderReportSortings,
  [SellerSettings.simpleOrderSummary.key]: trueFalse,
  [SellerSettings.orderSummaryScanToViewBarcode.key]: trueFalse,
  [SellerSettings.hideOrderReportImages.key]: trueFalse,
  [SellerSettings.orderSummaryMasterSku.key]: trueFalse,
  [SellerSettings.pickListGrouping.key]: pickListGroupings,
  [SellerSettings.pickListSorting.key]: pickListSortings,
  [SellerSettings.pickListImages.key]: trueFalse,
  [SellerSettings.pickListMasterSku.key]: trueFalse,
  [SellerSettings.pickListOrderNumbers.key]: trueFalse,
  [SellerSettings.pickListZeroQuantity.key]: trueFalse,
  [SellerSettings.pickListInventoryCounts.key]: trueFalse,
  [SellerSettings.pickListWarehouseLocation.key]: pickListWarehouseLocations,
  [SellerSettings.packingSlipShowDiscounts.key]: trueFalse,
  // TODO: [SPD-19601]: Use combined one when feature flag is removed
  [SellerSettings.packingSlipFormat.key]: [...defaultPackingSlipFormats, ...newPackingSlipFormats],
  [SellerSettings.packingSlipSorting.key]: packingSlipSortings,
  [SellerSettings.packingSlipLogos.key]: trueFalse,
  [SellerSettings.packingSlipPrices.key]: trueFalse,
  [SellerSettings.packingSlipSkus.key]: trueFalse,
  [SellerSettings.packingSlipBarcode.key]: trueFalse,
  [SellerSettings.packingSlipZeroQuantity.key]: trueFalse,
  [SellerSettings.labelFileType.key]: labelFileTypeValues.map((value) => ({
    value,
  })),
  [SellerSettings.packingSlipInventoryLocation.key]: trueFalse,
  [SellerSettings.packingSlipReturnQrCode.key]: trueFalse,
  [SellerSettings.serialNumberEnforcement.key]: [
    { value: SerialEnforcementLevel.Block },
    { value: SerialEnforcementLevel.Warn },
    { value: SerialEnforcementLevel.Allow },
  ],
  [SellerSettings.packingRecommendationEnforcementMode.key]: [
    { value: PackingEnforcementMode.Enforced },
    { value: PackingEnforcementMode.Warning },
    { value: PackingEnforcementMode.Advisory },
  ],
  [SellerSettings.labelOutput.key]: allLabelFormats,
  [SellerSettings.shipmentManifestItems.key]: trueFalse,
  [SellerSettings.labelDpi.key]: labelDpis,
  [SellerSettings.batchSummaryPage.key]: batchSummaryOptions,
  [SellerSettings.returnService.key]: [],
  [SellerSettings.returnPackage.key]: [],
  [SellerSettings.returnTracking.key]: trueFalse,
  [SellerSettings.AutoSplitEnabled.key]: trueFalse,
  [SellerSettings.labelPrintOrder.key]: labelOrderBys,
  [SellerSettings.enableLabelLogos.key]: trueFalse,
  [SellerSettings.extendShipDate.key]: trueFalse,
  [SellerSettings.setupComplete.key]: trueFalse,
  [SellerSettings.isQuickshipAllowed.key]: trueFalse,
  [SellerSettings.onboardLabelStepComplete.key]: trueFalse,
  [SellerSettings.customLabelLayouts.key]: [customLabelLayoutSettingOptions, []],
  [SellerSettings.sellerOnboardingVariant.key]: Object.values(EnabledOnboardingVariantTypes).map((value) => ({ value })),
  [SellerSettings.enableCubiscan.key]: trueFalse,
  [SellerSettings.isODBCEnabled.key]: trueFalse,
  [SellerSettings.isSmartRoutingEnabled.key]: trueFalse,
  [SellerSettings.isSmartOrderRoutingSetupComplete.key]: trueFalse,
  [SellerSettings.isCustomRateShopperSetupComplete.key]: trueFalse,
  [SellerSettings.isAutomationRulesSetupComplete.key]: trueFalse,
  [SellerSettings.isProductPresetsSetupComplete.key]: trueFalse,
  [SellerSettings.isServiceMappingsSetupComplete.key]: trueFalse,
  [SellerSettings.shouldUseFulfillmentProviders.key]: trueFalse,
  [SellerSettings.isAccountEmailVerified.key]: trueFalse,
  [SellerSettings.brandedTrackingEmailExperienceExisting.key]: trueFalse,
  [SellerSettings.brandedTrackingEmailExperienceNew.key]: trueFalse,
  [SellerSettings.pickListIncludeLotId.key]: trueFalse,
  [SellerSettings.pickListHideItemOptions.key]: trueFalse,
  [SellerSettings.isSmartFillActive.key]: trueFalse,
  [SellerSettings.shouldSmartFillOverwriteMarketplaceWeight.key]: trueFalse,
  [SellerSettings.totalOrderWithSmartFillApplied.key]: SellerSettingsOptions.empty,
  [SellerSettings.isSmartFillPackageEnabled.key]: trueFalse,
  [SellerSettings.isSmartFillSizeEnabled.key]: trueFalse,
  [SellerSettings.isSmartFillWeightEnabled.key]: trueFalse,
  [SellerSettings.isSmartFillHashKeyManagementEnabled.key]: trueFalse,
  [SellerSettings.mobileRequireLocationVerification.key]: trueFalse,
  [SellerSettings.mobileShowLocationStep.key]: trueFalse,
  [SellerSettings.isRefundAssistEnabled.key]: trueFalse,
  [SellerSettings.isTrialAutoConvertedToFreeNotificationPending.key]: trueFalse,
  [SellerSettings.billDutiesAndTaxesToPayor.key]: trueFalse,
  [SellerSettings.customsStoreCurrency.key]: trueFalse,
  [SellerSettings.unlockFulfilledByShipstationASN.key]: trueFalse,
  [SellerSettings.enableShipstationReturns.key]: trueFalse,
  [SellerSettings.isBuyNow.key]: trueFalse,
};

export const getSellerSettingOptions = (sellerSetting: SellerSettingKey): SellerSettingOptionSet => {
  return sellerSettingsOptionsMap[sellerSetting.key];
};

const isLabelledOption = (option: SellerSettingOption | unknown[]): option is LabelledSellerSettingOption =>
  !Array.isArray(option) && option.messageDescriptor !== undefined;

export const getLabelledSellerSettingOptions = (sellerSetting: SellerSettingKey): LabelledSellerSettingOption[] => {
  const optionSet = getSellerSettingOptions(sellerSetting);
  return (Array.isArray(optionSet) ? optionSet : [optionSet]).filter(isLabelledOption);
};

export const getSelectedOptionMessageDescriptor = (
  value: string | boolean,
  optionSet: SellerSettingOption[],
): MessageDescriptor | undefined => optionSet.find((option) => option.value === value)?.messageDescriptor;
